const GRAPH_BASE_URL = 'https://graph.microsoft.com/v1.0';

export interface DriveItem {
  id: string;
  name: string;
  size?: number;
  webUrl?: string;
  lastModifiedDateTime?: string;
  folder?: { childCount: number };
  file?: { mimeType: string };
  parentReference?: {
    driveId: string;
    id: string;
    path?: string;
  };
  remoteItem?: {
    id: string;
    name?: string;
    parentReference?: { driveId: string };
    folder?: { childCount: number };
    file?: { mimeType: string };
  };
}

export interface Drive {
  id: string;
  name: string;
  driveType: string;
  owner?: {
    user?: { displayName: string };
    group?: { displayName: string };
  };
}

export interface Site {
  id: string;
  name: string;
  displayName: string;
  webUrl: string;
}

interface GraphListResponse<T> {
  value: T[];
  '@odata.nextLink'?: string;
}

async function graphFetch<T>(accessToken: string, path: string): Promise<T> {
  const response = await fetch(`${GRAPH_BASE_URL}${path}`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Microsoft Graph API error (${response.status}): ${text}`);
  }

  return response.json();
}

export async function getMyDrive(token: string): Promise<Drive> {
  return graphFetch<Drive>(token, '/me/drive');
}

export async function getMyDriveRoot(token: string): Promise<DriveItem[]> {
  const data = await graphFetch<GraphListResponse<DriveItem>>(token, '/me/drive/root/children?$top=200&$orderby=name');
  return data.value;
}

export async function getDriveRoot(token: string, driveId: string): Promise<DriveItem[]> {
  const data = await graphFetch<GraphListResponse<DriveItem>>(token, `/drives/${driveId}/root/children?$top=200&$orderby=name`);
  return data.value;
}

export async function getDriveItemChildren(token: string, driveId: string, itemId: string): Promise<DriveItem[]> {
  const data = await graphFetch<GraphListResponse<DriveItem>>(token, `/drives/${driveId}/items/${itemId}/children?$top=200&$orderby=name`);
  return data.value;
}

export async function getSharedWithMe(token: string): Promise<DriveItem[]> {
  const data = await graphFetch<GraphListResponse<DriveItem>>(token, '/me/drive/sharedWithMe');
  return data.value;
}

export async function getSharePointSites(token: string): Promise<Site[]> {
  const data = await graphFetch<GraphListResponse<Site>>(token, '/sites?search=*&$top=100');
  return data.value;
}

export async function getSiteDrives(token: string, siteId: string): Promise<Drive[]> {
  const data = await graphFetch<GraphListResponse<Drive>>(token, `/sites/${siteId}/drives`);
  return data.value;
}

export async function searchDrive(token: string, driveId: string, query: string): Promise<DriveItem[]> {
  const data = await graphFetch<GraphListResponse<DriveItem>>(token, `/drives/${driveId}/root/search(q='${encodeURIComponent(query)}')?$top=50`);
  return data.value;
}

export async function getDriveItemPath(token: string, driveId: string, itemId: string): Promise<string> {
  const item = await graphFetch<DriveItem>(token, `/drives/${driveId}/items/${itemId}`);
  if (item.parentReference?.path) {
    const match = item.parentReference.path.match(/\/root:(.+)/);
    if (match) {
      return match[1].replace(/^\//, '') + '/' + item.name;
    }
  }
  return item.name;
}
